import { useNotify } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useAgents } from "../useAgents";
import { missionName, type CallMission } from "./callPlan";

const dayFromToday = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const DAYS = [
  { label: "Hoy", days: 0 },
  { label: "Mañana", days: 1 },
  { label: "Pasado mañana", days: 2 },
];

const chip = (active: boolean) =>
  cn(
    "rounded-full border px-3 py-1.5 text-sm",
    active
      ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
      : "hover:bg-muted",
  );

/**
 * Admins pass a lead of Mi jornada to someone (Mica, a setter…) for a day:
 * it becomes theirs and shows in their list that day.
 */
export const AssignDialog = ({
  mission,
  onClose,
  onDone,
}: {
  mission: CallMission | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const notify = useNotify();
  const { workers, meId } = useAgents();
  const [salesId, setSalesId] = useState<string>("");
  const [day, setDay] = useState(dayFromToday(1));
  const [saving, setSaving] = useState(false);
  if (!mission) return null;

  const people = workers.filter((agent) => String(agent.id) !== String(meId));
  const save = async () => {
    if (!salesId) {
      notify("Elige a quién se lo pasas", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const { error } = await getSupabaseClient().rpc("gula_assign_call", {
        p_client_id: Number(mission.client_id),
        p_sales_id: Number(salesId),
        p_day: day,
      });
      if (error) throw error;
      const name = people.find(
        (agent) => String(agent.id) === salesId,
      )?.first_name;
      notify(
        `${missionName(mission)} pasa a ${name ?? "su lista"} ${
          day === dayFromToday(0)
            ? "hoy"
            : day === dayFromToday(1)
              ? "mañana"
              : `el ${new Date(`${day}T12:00:00`).toLocaleDateString("es-ES")}`
        }`,
        { type: "success" },
      );
      onDone();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido pasar", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pasar {missionName(mission)}</DialogTitle>
          <DialogDescription>
            Pasa a ser de esa persona y le sale en su jornada el día que elijas.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">¿A quién?</span>
            <div className="flex flex-wrap gap-1.5">
              {people.map((agent) => (
                <button
                  key={String(agent.id)}
                  type="button"
                  onClick={() => setSalesId(String(agent.id))}
                  className={chip(salesId === String(agent.id))}
                >
                  {agent.first_name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">¿Para qué día?</span>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((option) => (
                <button
                  key={option.days}
                  type="button"
                  onClick={() => setDay(dayFromToday(option.days))}
                  className={chip(day === dayFromToday(option.days))}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Input
              type="date"
              min={dayFromToday(0)}
              value={day}
              onChange={(event) => setDay(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving}>
            Pasar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
